/**
 * useGuests — guests data hook backed by @v0y4ge/api-client.
 * No seed data in the UI. All data comes from the contract layer.
 *
 * Restored 2026-07-03 (Claude Code push-back, drop blocker 1a): re-added the
 * canonical `GuestDto` contract type and the create/update/delete mutation
 * calls this hook had dropped. This was list-only before; screens that let a
 * user edit/remove a guest now actually persist against the real API.
 */
import { useState, useEffect } from "react";
import type { GuestDto } from "@v0y4ge/api-contracts";
import { listGuests, createGuest, updateGuest, deleteGuest } from "@v0y4ge/api-client";

/** Payments (2026-08-04) — per-guest payment summary the Guests list uses for
 * the Paid/Refunded/Pending pill and the Refund/Cancel row actions. Optional;
 * only present once the guest DTO carries a `payment` block (or customFields). */
export interface GuestPayment {
  status?: string;
  amountMinor?: number;
  currency?: string;
  provider?: string;
  refundedMinor?: number;
  paymentId?: string | null;
}

export interface Guest {
  id: string; name: string; email: string; company?: string; ticket?: string;
  status: string; checked?: string; source: string; segments: string[];
  color?: string; eventId?: string;
  firstName?: string; lastName?: string; phone?: string;
  payment?: GuestPayment;
}
export interface GuestsState { guests: Guest[] | null; total: number; loading: boolean; }

/** Payments (2026-08-04): surface a guest's payment block wherever it rides —
 * a top-level `payment`, or nested under customFields (snake or camel). */
function guestPayment(g: Record<string, any>): GuestPayment | undefined {
  const p = g.payment ?? g.customFields?.payment ?? g.custom_fields?.payment;
  if (!p) return undefined;
  return {
    status: p.status,
    amountMinor: p.amountMinor ?? p.amount_minor,
    currency: p.currency,
    provider: p.provider,
    refundedMinor: p.refundedMinor ?? p.refunded_minor,
    paymentId: p.paymentId ?? p.payment_id ?? null,
  };
}

function toGuest(g: Record<string, any>): Guest {
  return {
    id: g.id,
    name: (`${g.firstName ?? g.first_name ?? ""}${g.lastName ?? g.last_name ? " " + (g.lastName ?? g.last_name) : ""}`).trim() || (g.email ?? "(no name)"),
    firstName: g.firstName ?? g.first_name ?? undefined,
    lastName: g.lastName ?? g.last_name ?? undefined,
    email: g.email ?? "",
    phone: g.phone ?? undefined,
    company: g.organization ?? g.company ?? undefined,
    ticket: g.ticketTier ?? g.ticket_tier ?? g.ticketName ?? g.ticket_name ?? undefined,
    status: g.status ?? "registered",
    checked: g.checkedInAt ?? g.checked_in_at ?? undefined,
    source: g.source ?? "web",
    segments: g.segment ? [g.segment] : (Array.isArray(g.tags) ? g.tags : []),
    eventId: g.eventId ?? g.event_id ?? undefined,
    color: g.color ?? undefined,
    payment: guestPayment(g),
  };
}

// GuestDto -> input field mapping for create/update (organization/ticketTier
// are UI-facing aliases of the canonical DTO's own field names).
function toInput(g: Partial<Guest>) {
  return {
    firstName: g.firstName, lastName: g.lastName, email: g.email, phone: g.phone,
    organization: g.company, ticketTier: g.ticket, status: g.status, eventId: g.eventId,
  };
}

export function useGuests(params: { eventId?: string; limit?: number; status?: string; search?: string } = {}) {
  const [state, setState] = useState<GuestsState>({ guests: null, total: 0, loading: true });
  const [nonce, setNonce] = useState(0);
  const key = JSON.stringify({ ...params, nonce });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    listGuests({ ...params, status: params.status as any, limit: String(params.limit ?? 200) })
      .then((res: any) => {
        if (cancelled) return;
        const raw: any[] = res.guests ?? res.data ?? [];
        setState({ guests: raw.map(toGuest), total: res.total ?? raw.length, loading: false });
      })
      .catch(() => { if (!cancelled) setState({ guests: [], total: 0, loading: false }); });
    return () => { cancelled = true; };
  }, [key]);

  const reload = () => setNonce((n) => n + 1);

  return {
    ...state,
    guests: state.guests ?? [],
    reload,
    /** Create (no id) or update (existing id) a guest via the real API, then reload. */
    upsertGuest: async (g: Partial<Guest> & { id?: string }) => {
      const isNew = !g.id;
      try {
        if (isNew && typeof createGuest === "function") {
          const res: any = await createGuest(toInput(g) as any);
          reload();
          return toGuest(res.guest ?? res);
        }
        if (!isNew && typeof updateGuest === "function") {
          const res: any = await updateGuest(g.id as string, toInput(g) as any);
          reload();
          return toGuest(res.guest ?? res);
        }
      } catch (e) { console.warn("Guest mutation failed:", e); }
      return null;
    },
    removeGuest: async (id: string) => {
      try {
        if (typeof deleteGuest === "function") await deleteGuest(id);
      } catch (e) { console.warn("deleteGuest failed:", e); }
      setState((s) => ({ ...s, guests: (s.guests ?? []).filter((x) => x.id !== id) }));
    },
  };
}

export type { GuestDto };
